/**
 * Champs texte « bufferisés » pour l'éditeur V2.
 *
 * Problème résolu : les anciens <input>/<textarea> appelaient onChange (=> une
 * mutation `exercise-blocks-mutate` + un round-trip edge function) à CHAQUE
 * frappe. Pénible et coûteux.
 *
 * Ici on garde une valeur de travail locale (`draft`). Rien n'est persisté tant
 * que l'utilisateur n'a pas VALIDÉ :
 *   - clic sur ✓ (ou Entrée pour un input, ⌘/Ctrl+Entrée pour un textarea)
 *   - ou simplement en quittant le champ (blur)
 * Il peut ANNULER ses modifications locales via ✗ (ou Échap).
 *
 * Les deux boutons ✓ / ✗ n'apparaissent QUE lorsque la valeur a changé.
 */

import { useRef, useState } from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '@jeraisonne/ui';

/**
 * Hook partagé : maintient un brouillon local synchronisé avec la valeur
 * persistée passée en prop. Quand la valeur sauvegardée change de l'extérieur
 * (refetch après mutation, dictée, génération…), le brouillon se réaligne.
 *
 * `sentRef` déduplique les commits : valider via Entrée puis blur, ou deux blurs
 * successifs, ne déclenche qu'UNE seule mutation.
 */
export function useBufferedValue(value: string, onCommit: (v: string) => void) {
  const [draft, setDraft] = useState(value);
  const [base, setBase] = useState(value);
  const sentRef = useRef(value);

  // Resync pendant le render quand la valeur persistée change réellement.
  if (value !== base) {
    setBase(value);
    setDraft(value);
    sentRef.current = value;
  }

  const dirty = draft !== value;

  const commit = () => {
    if (draft !== sentRef.current) {
      sentRef.current = draft;
      onCommit(draft);
    }
  };

  const reset = () => {
    setDraft(value);
    sentRef.current = value;
  };

  return { draft, setDraft, dirty, commit, reset };
}

/**
 * Boutons ✓ / ✗ affichés à côté d'un champ modifié.
 * `onMouseDown` preventDefault : éviter que le clic ne « blur » d'abord le champ
 * (ce qui déclencherait un commit avant le clic, court-circuitant Annuler).
 */
export function CommitButtons({
  onCommit,
  onCancel,
  disabled,
}: {
  onCommit: () => void;
  onCancel: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex shrink-0 gap-1">
      <button
        type="button"
        aria-label="Valider la modification"
        title="Valider"
        onMouseDown={(e) => e.preventDefault()}
        onClick={onCommit}
        disabled={disabled}
        className="inline-flex min-h-9 min-w-9 items-center justify-center rounded-md border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 active:scale-95 disabled:opacity-50"
      >
        <Check className="h-4 w-4" />
      </button>
      <button
        type="button"
        aria-label="Annuler la modification"
        title="Annuler"
        onMouseDown={(e) => e.preventDefault()}
        onClick={onCancel}
        disabled={disabled}
        className="inline-flex min-h-9 min-w-9 items-center justify-center rounded-md border border-slate-300 bg-slate-50 text-slate-500 hover:bg-slate-100 active:scale-95 disabled:opacity-50"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export function BufferedTextField({
  value,
  onChange,
  disabled,
  mono,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  mono?: boolean;
  placeholder?: string;
}) {
  const { draft, setDraft, dirty, commit, reset } = useBufferedValue(value, onChange);
  return (
    <div className="flex items-center gap-1.5">
      <input
        className={cn(
          'w-full rounded-md border bg-white px-2 py-1.5 text-sm',
          'focus:outline-none focus:ring-1 focus:ring-slate-500',
          mono && 'font-mono',
          dirty ? 'border-emerald-400 ring-1 ring-emerald-200' : 'border-slate-300',
          disabled && 'opacity-60',
        )}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            reset();
          }
        }}
        disabled={disabled}
        placeholder={placeholder}
      />
      {dirty ? <CommitButtons onCommit={commit} onCancel={reset} disabled={disabled} /> : null}
    </div>
  );
}

export function BufferedTextArea({
  value,
  onChange,
  disabled,
  rows = 3,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  rows?: number;
  placeholder?: string;
}) {
  const { draft, setDraft, dirty, commit, reset } = useBufferedValue(value, onChange);
  return (
    <div className="space-y-1.5">
      <textarea
        className={cn(
          'w-full rounded-md border bg-white px-2 py-1.5 font-mono text-sm',
          'focus:outline-none focus:ring-1 focus:ring-slate-500',
          dirty ? 'border-emerald-400 ring-1 ring-emerald-200' : 'border-slate-300',
          disabled && 'opacity-60',
        )}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            reset();
          }
        }}
        rows={rows}
        disabled={disabled}
        placeholder={placeholder}
      />
      {dirty ? (
        <div className="flex items-center gap-2">
          <CommitButtons onCommit={commit} onCancel={reset} disabled={disabled} />
          <span className="text-[11px] text-slate-400">
            ⌘/Ctrl + Entrée pour valider · Échap pour annuler
          </span>
        </div>
      ) : null}
    </div>
  );
}
